import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Clock, AlertTriangle } from "lucide-react";
import { cn } from "@/components/utils/utils";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useI18n } from "@/hooks/useI18n";

interface ExpiryCountdownProps {
  createdAt: string;
  status?: string;
  className?: string;
  variant?: "card" | "table";
  expiryHours?: number;
}

export const ExpiryCountdown: React.FC<ExpiryCountdownProps> = ({
  createdAt,
  status,
  className,
  variant = "card",
  expiryHours = 48
}) => {
  const { t } = useI18n();
  const [timeLeft, setTimeLeft] = useState<{
    hours: number;
    minutes: number;
    isExpired: boolean;
    isUrgent: boolean;
  }>({ hours: 0, minutes: 0, isExpired: false, isUrgent: false });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const created = new Date(createdAt);
      const expiry = new Date(created.getTime() + expiryHours * 60 * 60 * 1000); // Ore configurabili
      const now = new Date();
      const diff = expiry.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, isExpired: true, isUrgent: false });
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const isUrgent = hours < 6; // Urgente se mancano meno di 6 ore

      setTimeLeft({ hours, minutes, isExpired: false, isUrgent });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 60000); // Aggiorna ogni minuto

    return () => clearInterval(interval);
  }, [createdAt, expiryHours]);

  // Non mostrare il countdown se lo stato non è pending (DOPO gli hooks!)
  if (status !== 'pending') {
    return null;
  }

  const getTooltipContent = () => {
    const created = new Date(createdAt);
    const expiry = new Date(created.getTime() + expiryHours * 60 * 60 * 1000);

    if (timeLeft.isExpired) {
      return `Scaduta il ${expiry.toLocaleDateString('it-IT')}`;
    }

    return `Scade il ${expiry.toLocaleDateString('it-IT')} alle ${expiry.toLocaleTimeString('it-IT')}`;
  };

  if (timeLeft.isExpired) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-block">
            <div className={cn(
              "px-3 py-1 bg-red-100 border border-red-300 rounded-full shadow-sm flex items-center gap-2",
              className
            )}>
              <span className="text-sm font-medium text-red-800">{t('travelRequest.statusExpired')}</span>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {getTooltipContent()}
        </TooltipContent>
      </Tooltip>
    );
  }

  const formatTime = () => {
    if (timeLeft.hours > 0) {
      return `${timeLeft.hours}h`;
    }
    return `${timeLeft.minutes}m`;
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-block">
            <Badge
              variant={timeLeft.isUrgent ? "destructive" : "secondary"}
              className={cn(
                "flex items-center gap-1 text-sm cursor-default",
                variant === "table" ? "px-2 py-1" : "px-2 py-1",
                className
              )}
            >
              <Clock className="h-3 w-3" />
              {formatTime()}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {getTooltipContent()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
